/**
 * The outstanding-request state machine behind the lift drawer's verb
 * buttons (landing plan 2026-08-29, S3; critic gaps 7 and 8).
 *
 * WHY THIS EXISTS. Every operator verb used to end at the CommandRecord,
 * which freezes at "accepted" the moment the worker publishes -- so a
 * Release refused by the edge within the same second showed "Accepted —
 * awaiting confirmation" until the operator gave up. Admission is not
 * the outcome. The outcome is the adapter writing
 * `LiftState.session.last_result` keyed by the api-server-minted
 * `request_id` the press was answered with, and this hook is the ONLY
 * place that correlation happens.
 *
 *   idle ──press resolves──▶ inflight{verb, request_id, sentAt, deadline_s}
 *                                 │ last_result.request_id === request_id  → verdict{...last_result}
 *                                 │ deadline_s + 5 s elapsed               → verdict{timeout, NO_VERDICT}
 *   idle ──press rejected (409 / HTTP error)────────────────────────────────▶ verdict (no in-flight state)
 *   verdict ──dismiss / next press──▶ idle / inflight
 *
 * The 5 s grace over `deadline_s` covers the projection path (adapter →
 * worker → zenoh → SSE) which is event-driven and takes well under a
 * second; NO_VERDICT therefore means the verdict path is broken, not
 * that the lift is slow, and the banner says so.
 *
 * GAP 7: a response whose `request_id` ALREADY equals `last_result`'s is
 * a verdict immediately. That happens when the api-server replays an
 * idempotent record whose verdict landed earlier; waiting for a
 * `last_result` that is already there would sit until NO_VERDICT.
 */
import { isPreviewModeError } from '../../auth/role-preview';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { LiftViewModel } from '../../api/adapters';
import type {
  LiftOperatorVerb,
  LiftSessionResult,
  LiftSessionResultStatus,
  LiftVerbResponse,
} from '../../api/v1';
import { ApiClientError } from '../../api/v1-client';

/** Seconds past the api-server's `deadline_s` before the drawer gives up. */
export const NO_VERDICT_GRACE_S = 5;

export interface OperatorVerdict {
  readonly kind: 'verdict';
  readonly verb: string;
  readonly request_id: string | null;
  readonly status: LiftSessionResultStatus;
  readonly code: string | null;
  readonly reason: string;
  /** The wait / phase a refusal named, when it named one -- the
   *  operator needs to know WHICH step said no. */
  readonly wait_name: string | null;
  readonly phase: string | null;
  /** Wall-clock ms at which the verdict was recorded here. */
  readonly ts: number;
  /** True when the api-server answered before anything reached the
   *  adapter (a 409 pre-flight or a transport error): there was never
   *  an in-flight request, so there is nothing to wait for. */
  readonly preflight: boolean;
}

export type OperatorRequestState =
  | { readonly kind: 'idle' }
  | {
      readonly kind: 'inflight';
      readonly verb: LiftOperatorVerb;
      readonly request_id: string;
      readonly sentAt: number;
      readonly deadline_s: number;
    }
  | OperatorVerdict;

const IDLE: OperatorRequestState = { kind: 'idle' };

/**
 * The five verbs' short names as they appear in idempotency keys and
 * request ids (`advance-<hex>`, `call-<hex>`, ...).
 */
export type OperatorKeyVerb = 'advance' | 'recover' | 'end' | 'call' | 'close';

/**
 * One operator PRESS, as a short random token (spa 0.5.40).
 *
 * Minted by the drawer when a confirm dialog opens (or, for Cancel call,
 * which has no dialog, when the press first runs) and dropped when that
 * press's request settles. Everything that fires for the same press -- a
 * double-tap, two ConfirmDialog keydown listeners answering one Enter --
 * reads the SAME nonce, so it still collapses to one key and one record.
 * The next press gets a new one.
 *
 * `crypto.getRandomValues` where it exists; the fallback is only for a
 * runtime without WebCrypto, where uniqueness per press is all that is
 * needed -- this is not a secret, it is a press counter nobody else can
 * predict closely enough to collide with.
 */
export function mintPressNonce(): string {
  const bytes = new Uint8Array(6);
  const c = (globalThis as { crypto?: Crypto }).crypto;
  if (c && typeof c.getRandomValues === 'function') {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * Derived from the lift, the verb, its context, the last verdict AND the
 * press (S6, revised 2026-09-15).
 *
 * `last_result.ts` is folded in (gap 7) because the api-server replayed a
 * repeated key FOREVER: with `${lift}:call:${floor}:${lease}` alone, a
 * call refused WRITES_NOT_ARMED, then the site armed, then the same
 * press within the lease → the old record replayed, nothing published,
 * and the drawer waiting on a verdict that had already landed. Once a
 * verdict lands the key changes, so a re-press is a new request.
 *
 * `pressNonce` is folded in because `last_result.ts` is not enough. When
 * the adapter restarts (or forgets the ride) the projection carries no
 * `last_result`, the key ends `:0`, and it is the same `:0` key every
 * End session / Recover on that robot has ever had. sg-nomad-01's
 * `command_intents` already held `SL1:end:phantas_fleet/phantas_01:0`
 * from 2026-08-31 and `SL1:recover:phantas_fleet/phantas_01:0` from
 * 2026-09-04, so both 2026-09-14 presses were answered 200 with those
 * old records: no intent row, nothing published, no supervisor END, and
 * a success-shaped response on the operator's screen. The nonce makes
 * every press unique while a double-tap of the same press still shares
 * one key (see `mintPressNonce`). api-server 0.6.33 also refuses a
 * replay older than its short window with `idempotency_key_stale`, so an
 * installed PWA still running the old key shape gets an error instead.
 */
export function operatorIdempotencyKey(
  lift: Pick<LiftViewModel, 'name' | 'session'>,
  verb: OperatorKeyVerb,
  ctx: string,
  pressNonce: string,
): string {
  return `${lift.name}:${verb}:${ctx}:${lift.session?.last_result?.ts ?? 0}:${pressNonce}`;
}

export function verdictFromResult(
  r: LiftSessionResult,
  phase: string | null | undefined,
): OperatorVerdict {
  return {
    kind: 'verdict',
    verb: r.verb,
    request_id: r.request_id,
    status: r.status,
    code: r.code ?? null,
    reason: r.reason,
    wait_name: null,
    phase: phase ?? null,
    ts: Date.now(),
    preflight: false,
  };
}

function firstString(...candidates: unknown[]): string | null {
  for (const c of candidates) if (typeof c === 'string' && c.length > 0) return c;
  return null;
}

function asRecord(v: unknown): Record<string, unknown> | undefined {
  return v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined;
}

/**
 * A press the api-server refused before it reached ROS (P3's 409 with
 * `{code, reason, phase, wait, owner}`), or that never got an answer at
 * all. Read the fields from wherever the envelope put them -- top level,
 * FastAPI's `detail`, or the SPA's own `detail` -- because the shape is
 * owned by another repo and a verdict that loses its reason is exactly
 * the silence this hook exists to end.
 *
 * `verb` is a plain string, not `LiftOperatorVerb`: the task screens'
 * control verbs (cancel/kill/interrupt/resume) reuse this reader
 * verbatim so a 409 from `POST .../tasks/{id}/control` is unpacked by
 * the same code that unpacks a lift's, rather than by a second,
 * subtly-different one. `OperatorVerdict.verb` was already `string`.
 */
export function verdictFromError(verb: string, err: unknown): OperatorVerdict {
  const ts = Date.now();
  // "View as role": the client refused to send it. Not a failure of the
  // path and not the adapter's refusal either -- a calm "actions are
  // off" in the verdict's own place.
  if (isPreviewModeError(err)) {
    return {
      kind: 'verdict',
      verb,
      request_id: null,
      status: 'refused',
      code: 'PREVIEW_MODE',
      reason: err.message,
      wait_name: null,
      phase: null,
      ts,
      preflight: true,
    };
  }
  if (err instanceof ApiClientError) {
    const top = asRecord(err.body) ?? {};
    const nested = asRecord(top.detail) ?? err.detail ?? {};
    const wait = asRecord(nested.wait) ?? asRecord(top.wait);
    const reason =
      firstString(nested.reason, top.reason, nested.message, top.message) ??
      (err.message !== 'api error' ? err.message : `HTTP ${err.status}`);
    return {
      kind: 'verdict',
      verb,
      request_id: null,
      // 409 is the api-server's pre-flight: the verb WOULD be refused by
      // the adapter, said early. Anything else is transport/authz, which
      // is a failure of the path rather than a refusal of the verb.
      status: err.status === 409 ? 'refused' : 'failed',
      code:
        err.code === 'internal'
          ? (firstString(nested.code, top.code) ?? `HTTP_${err.status}`)
          : err.code,
      reason,
      wait_name: firstString(wait?.name),
      phase: firstString(nested.phase, top.phase),
      ts,
      preflight: true,
    };
  }
  return {
    kind: 'verdict',
    verb,
    request_id: null,
    status: 'failed',
    code: 'NETWORK',
    reason: err instanceof Error ? err.message : 'request failed',
    wait_name: null,
    phase: null,
    ts,
    preflight: true,
  };
}

export function useOperatorRequest(lift: LiftViewModel, now: number) {
  const [state, setState] = useState<OperatorRequestState>(IDLE);
  const lastResult = lift.session?.last_result ?? null;
  const phase = lift.session?.phase ?? lift.sessionPhase ?? null;
  // `begin` runs inside a mutation callback, which closes over the
  // render that pressed the button; the projection may have moved since.
  // A ref keeps the gap-7 check honest against the LATEST last_result.
  const lastResultRef = useRef(lastResult);
  lastResultRef.current = lastResult;
  const phaseRef = useRef(phase);
  phaseRef.current = phase;

  const begin = useCallback((verb: LiftOperatorVerb, res: LiftVerbResponse) => {
    // An api-server older than 0.6.8 answers `{command}` alone: no
    // request_id to correlate on and no deadline. Entering `inflight`
    // on that would count down from NaN and report NO_VERDICT for a
    // command the worker accepted. Say what actually happened instead.
    // Only reachable when the rollout order (api-server before SPA) is
    // violated, which is exactly when a clear message matters most.
    if (typeof res?.request_id !== 'string' || typeof res?.deadline_s !== 'number') {
      setState({
        kind: 'verdict',
        verb,
        request_id: null,
        status: 'failed',
        code: 'API_TOO_OLD',
        reason:
          'The api-server predates 0.6.8 and returned no request id: the command was ' +
          'accepted but there is no verdict path. Check the lift state directly.',
        wait_name: null,
        phase: phaseRef.current,
        ts: Date.now(),
        preflight: true,
      });
      return;
    }
    const lr = lastResultRef.current;
    if (lr && lr.request_id === res.request_id) {
      setState(verdictFromResult(lr, phaseRef.current));
      return;
    }
    setState({
      kind: 'inflight',
      verb,
      request_id: res.request_id,
      sentAt: Date.now(),
      deadline_s: res.deadline_s,
    });
  }, []);

  const fail = useCallback((verb: LiftOperatorVerb, err: unknown) => {
    setState(verdictFromError(verb, err));
  }, []);

  const dismiss = useCallback(() => setState(IDLE), []);

  // The verdict, from the projection. Keyed on request_id and nothing
  // else: a `last_result` for an EARLIER press (or another operator's)
  // must not end this one's wait.
  useEffect(() => {
    if (state.kind !== 'inflight') return;
    if (lastResult && lastResult.request_id === state.request_id) {
      setState(verdictFromResult(lastResult, phase));
    }
  }, [state, lastResult, phase]);

  // The give-up. `now` is the drawer's 1 Hz clock, so this re-evaluates
  // every second without a timer of its own.
  useEffect(() => {
    if (state.kind !== 'inflight') return;
    // A real verdict in THIS commit wins. Both effects close over the
    // same `inflight` state; when the projection carrying last_result
    // and the tick that crosses deadline_s + 5 are batched into one
    // render, the verdict effect above fires first and this one would
    // otherwise overwrite its answer with NO_VERDICT -- the operator
    // reads "No verdict reached the console" with the verdict on screen.
    if (lastResult && lastResult.request_id === state.request_id) return;
    const budgetMs = (state.deadline_s + NO_VERDICT_GRACE_S) * 1000;
    if (now - state.sentAt < budgetMs) return;
    const timedOut: OperatorRequestState = {
      kind: 'verdict',
      verb: state.verb,
      request_id: state.request_id,
      status: 'timeout',
      code: 'NO_VERDICT',
      reason:
        `No verdict reached the console within ${state.deadline_s + NO_VERDICT_GRACE_S}s. ` +
        'The request may still have been acted on; the verdict path (adapter → worker → ' +
        'projection) is not delivering. Check the lift state before pressing again.',
      wait_name: null,
      phase,
      ts: now,
      preflight: false,
    };
    // Functional, and only for the request this effect was evaluated
    // for: a give-up can never clobber a verdict (or a per-lift reset)
    // that an earlier updater in the same batch already applied.
    setState((s) => (s.kind === 'inflight' && s.request_id === state.request_id ? timedOut : s));
  }, [state, now, phase, lastResult]);

  // The state is PER LIFT, but this hook lives in a drawer whose `lift`
  // prop can change in place: DataTable rows are focusable
  // (design-system/components/DataTable.tsx tabIndex=0) and Enter fires
  // onRowClick -> setSelectedLiftName(other), while useDismissOnOutside
  // only unmounts on mousedown. Without this, the second lift inherits
  // the first lift's in-flight state -- every verb disabled, an in-flight
  // line naming a request_id it never sent, and at deadline_s + 5 a
  // NO_VERDICT banner on the wrong lift -- while the first lift's real
  // verdict is dropped. The mount sites also carry `key={lift.name}` so
  // the whole drawer remounts; this is the in-hook guarantee for any
  // mount site that does not. The first lift's verdict is deliberately
  // dropped rather than shown here: a verdict for PL3 on SL1's drawer
  // is a misattribution, which is worse than an absence. Declared LAST
  // so that, when the name change and the NO_VERDICT tick land in one
  // commit, the reset is the state update that wins.
  const liftNameRef = useRef(lift.name);
  useEffect(() => {
    if (liftNameRef.current === lift.name) return;
    liftNameRef.current = lift.name;
    setState(IDLE);
  }, [lift.name]);

  return { state, begin, fail, dismiss };
}
